// Hook для отслеживания прогресса фоновых задач

import { useState, useCallback, useRef, useEffect } from 'react';
import { tasksApi } from '../services/api';
import type { BackgroundTask } from '../types';

export function useTaskPolling() {
  const [taskId, setTaskId] = useState<string | null>(null);
  const [task, setTask] = useState<BackgroundTask | null>(null);
  const [progress, setProgress] = useState(0);
  const [isPolling, setIsPolling] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const startPolling = useCallback((newTaskId: string) => {
    setTaskId(newTaskId);
    setIsPolling(true);
    setProgress(0);

    const poll = async () => {
      try {
        const status = await tasksApi.getStatus(newTaskId);
        setTask(status);

        if (status.status === 'completed') {
          setProgress(100);
          setIsPolling(false);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        } else if (status.status === 'failed') {
          setIsPolling(false);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        } else {
          // Still running, update progress
          setProgress((prev) => Math.min(prev + 2, 90));
        }
      } catch (error) {
        console.error('Error polling task status:', error);
      }
    };

    // Initial poll
    poll();

    // Start polling interval
    intervalRef.current = setInterval(poll, 1000);
  }, []);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPolling(false);
    setTaskId(null);
    setTask(null);
    setProgress(0);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    taskId,
    task,
    progress,
    isPolling,
    startPolling,
    stopPolling,
  };
}
